import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useJobs } from "@/lib/api/hooks";
import { StatusBadge } from "@/components/status-badge";
import { formatDuration, formatRelative, formatTokens } from "@/lib/format";
import type { JobStatus } from "@/lib/mock/types";

export const Route = createFileRoute("/_authenticated/jobs")({
  component: JobsPage,
  head: () => ({ meta: [{ title: "Jobs — CSaaS" }] }),
});

function JobsPage() {
  const { data: jobs = [], isLoading } = useJobs();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<JobStatus | "all">("all");

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (status !== "all" && j.status !== status) return false;
      if (q && !`${j.skillName} ${j.prompt}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [jobs, q, status]);

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Jobs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          All skill executions across your workspace.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Search prompt or skill…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <Select value={status} onValueChange={(v) => setStatus(v as JobStatus | "all")}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-medium">Job</th>
                  <th className="px-4 py-2.5 font-medium">Skill</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium text-right">Duration</th>
                  <th className="px-4 py-2.5 font-medium text-right">Tokens</th>
                  <th className="px-4 py-2.5 font-medium text-right">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                      {isLoading ? "Loading…" : "No jobs match."}
                    </td>
                  </tr>
                )}
                {filtered.map((j) => (
                  <tr
                    key={j.id}
                    className="border-t border-border hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3 max-w-sm">
                      <Link to="/jobs/$id" params={{ id: j.id }} className="block">
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {j.id.slice(0, 8)}
                        </div>
                        <div className="truncate text-foreground">{j.prompt}</div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs font-mono">{j.skillName}</code>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {formatDuration(j.durationMs)}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {formatTokens(j.tokensUsed)}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground whitespace-nowrap">
                      {formatRelative(j.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
